/** SOP Centre building blocks — icon map, section renderers, rating. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, ClipboardList, ShoppingCart, Warehouse, Settings2, Send, Truck, PackageCheck, MonitorSmartphone, Undo2, LifeBuoy,
  Recycle, Landmark, UserPlus, UserMinus, Building2, Smartphone, Sparkles, CheckSquare, BookOpen, ArrowUpRight, ThumbsUp, ThumbsDown,
  AlertTriangle, ShieldCheck, HelpCircle, Users, ChevronDown,
} from "lucide-react";

export const ICONS = { FileText, ClipboardList, ShoppingCart, Warehouse, Settings2, Send, Truck, PackageCheck, MonitorSmartphone, Undo2, LifeBuoy, Recycle, Landmark, UserPlus, UserMinus, Building2, Smartphone, Sparkles, CheckSquare, BookOpen };
export const SopIcon = ({ name, className = "h-4 w-4" }) => { const I = ICONS[name] || BookOpen; return <I className={className} strokeWidth={1.8} />; };

export const GROUP_TONE = { Sales: "bg-[#E6F4FF] text-[#246BFD]", Operations: "bg-[#FFF4E0] text-[#B25A00]", Logistics: "bg-[#EEE9FF] text-[#5B3FD6]", ITAM: "bg-[#E3F5EE] text-[#169C70]", Service: "bg-[#FDE8E8] text-[#C2333F]", Finance: "bg-[#E8F7F5] text-[#0E7C86]", Lifecycle: "bg-[#F1EEFF] text-[#6D3FD6]", Customer: "bg-[#FFF0F6] text-[#B8286B]", AI: "bg-[#EEE9FF] text-[#5B3FD6]", Home: "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text)]" };

export function SectionTitle({ icon: I, children, count }) {
  return <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.12em] uppercase text-[color:var(--st-text-muted)]"><I className="h-3.5 w-3.5" /> {children}{count != null && <span className="ml-1 rounded-full bg-[color:var(--st-surface-alt)] px-1.5 text-[10px]">{count}</span>}</div>;
}

export function WorkflowStrip({ stages = [] }) {
  if (!stages.length) return null;
  return (
    <ol className="flex flex-wrap items-center gap-1.5" data-testid="sop-workflow">
      {stages.map((s, i) => <li key={s + i} className="flex items-center gap-1.5"><span className="rounded-md border border-[color:var(--st-border)] bg-white px-2 py-1 text-[11px] font-medium whitespace-nowrap">{s}</span>{i < stages.length - 1 && <span className="text-[color:var(--st-text-subtle)] text-[11px]">›</span>}</li>)}
    </ol>
  );
}

export function StepList({ steps = [], compact = false, onNavigate }) {
  const navigate = useNavigate();
  const go = (r) => { onNavigate?.(); navigate(r); };
  return (
    <ol className="space-y-2" data-testid="sop-steps">
      {steps.map((s, i) => (
        <li key={i} className={"flex gap-3 " + (compact ? "" : "os-card p-3")} data-testid={`sop-step-${i + 1}`}>
          <span className="h-6 w-6 shrink-0 rounded-full bg-[color:var(--st-text)] text-white text-[11px] font-bold grid place-items-center">{i + 1}</span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold leading-tight">{s.title}</div>
            {s.detail && <div className="text-[12px] text-[color:var(--st-text-muted)] mt-0.5 leading-relaxed">{s.detail}</div>}
          </div>
          {s.route && <button onClick={() => go(s.route)} className="self-start shrink-0 inline-flex items-center gap-1 h-7 px-2 rounded-full border border-[color:var(--st-border)] bg-white text-[11px] font-medium hover:border-[color:var(--st-accent)] hover:text-[color:var(--st-accent)]" data-testid={`sop-step-open-${i + 1}`}>Open <ArrowUpRight className="h-3 w-3" /></button>}
        </li>
      ))}
    </ol>
  );
}

export function ExceptionsTable({ rows = [] }) {
  if (!rows.length) return <p className="text-[12px] text-[color:var(--st-text-muted)]">None recorded.</p>;
  return (
    <div className="os-card overflow-hidden" data-testid="sop-exceptions">
      <table className="w-full text-[12.5px]"><thead className="bg-[color:var(--st-surface-alt)] text-[10px] uppercase tracking-[0.12em] text-[color:var(--st-text-muted)]"><tr><th className="text-left px-3 py-2 w-2/5">If this happens</th><th className="text-left px-3 py-2">Do this</th></tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-[color:var(--st-border)]"><td className="px-3 py-2 font-medium">{r.issue}</td><td className="px-3 py-2 text-[color:var(--st-text-muted)]">{r.resolution}</td></tr>)}</tbody></table>
    </div>
  );
}

export function FaqList({ items = [] }) {
  const [open, setOpen] = useState(null);
  if (!items.length) return <p className="text-[12px] text-[color:var(--st-text-muted)]">No FAQ yet.</p>;
  return (
    <div className="divide-y divide-[color:var(--st-border)] os-card" data-testid="sop-faq">
      {items.map((f, i) => (
        <div key={i}><button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-[13px] font-medium" data-testid={`sop-faq-${i}`}>{f.q}<ChevronDown className={"h-4 w-4 shrink-0 transition-transform " + (open === i ? "rotate-180" : "")} /></button>
          {open === i && <div className="px-3 pb-3 text-[12.5px] text-[color:var(--st-text-muted)] leading-relaxed">{f.a}</div>}</div>
      ))}
    </div>
  );
}

export function Rating({ sop, path }) {
  const [done, setDone] = useState(null);
  const rate = async (helpful) => {
    try { await api.post(`/sop/modules/${sop.key}/rate`, { helpful, path, comment: helpful ? null : window.prompt("What was missing or unclear? (optional)") || null }); setDone(helpful); toast.success("Thanks — recorded"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="flex items-center gap-2 text-[12px] text-[color:var(--st-text-muted)]" data-testid="sop-rating">
      Was this helpful?
      <button onClick={() => rate(true)} disabled={done != null} className={"h-7 px-2.5 rounded-full border inline-flex items-center gap-1 " + (done === true ? "bg-[#E3F5EE] border-[#169C70] text-[#169C70]" : "border-[color:var(--st-border)] bg-white hover:border-[#169C70]")} data-testid="sop-rate-yes"><ThumbsUp className="h-3 w-3" /> Yes</button>
      <button onClick={() => rate(false)} disabled={done != null} className={"h-7 px-2.5 rounded-full border inline-flex items-center gap-1 " + (done === false ? "bg-[#FDE8E8] border-[#C2333F] text-[#C2333F]" : "border-[color:var(--st-border)] bg-white hover:border-[#C2333F]")} data-testid="sop-rate-no"><ThumbsDown className="h-3 w-3" /> No</button>
    </div>
  );
}

/** Full article body (used by the page and, compact, by the ? help sheet). */
export function SopBody({ sop, compact = false, onNavigate, path }) {
  return (
    <div className="space-y-6" data-testid={`sop-body-${sop.key}`}>
      <section className="grid md:grid-cols-[1.4fr_1fr] gap-4">
        <div className="os-card p-4"><SectionTitle icon={HelpCircle}>What is it</SectionTitle><p className="text-[13px] mt-2 leading-relaxed">{sop.what_is_it}</p></div>
        <div className="os-card p-4"><SectionTitle icon={Users}>Who uses it</SectionTitle><ul className="mt-2 space-y-1">{(sop.who_uses_it || []).map((w) => <li key={w} className="text-[12.5px] flex gap-2"><span className="text-[color:var(--st-accent)]">•</span>{w}</li>)}</ul></div>
      </section>
      <section className="space-y-2"><SectionTitle icon={ClipboardList}>Workflow</SectionTitle><WorkflowStrip stages={sop.workflow} /></section>
      <section className="space-y-2"><SectionTitle icon={CheckSquare} count={(sop.steps || []).length}>Step by step</SectionTitle><StepList steps={sop.steps} compact={compact} onNavigate={onNavigate} /></section>
      <section className="grid md:grid-cols-2 gap-4">
        <div className="os-card p-4"><SectionTitle icon={ShieldCheck}>Approval process</SectionTitle><p className="text-[12.5px] mt-2 text-[color:var(--st-text-muted)] leading-relaxed">{sop.approval_process || "None."}</p></div>
        <div className="os-card p-4"><SectionTitle icon={ArrowUpRight}>Escalation</SectionTitle><p className="text-[12.5px] mt-2 text-[color:var(--st-text-muted)] leading-relaxed">{sop.escalation || "—"}</p></div>
      </section>
      <section className="space-y-2"><SectionTitle icon={AlertTriangle} count={(sop.exceptions || []).length}>Common exceptions</SectionTitle><ExceptionsTable rows={sop.exceptions} /></section>
      <section className="space-y-2"><SectionTitle icon={HelpCircle} count={(sop.faq || []).length}>FAQ</SectionTitle><FaqList items={sop.faq} /></section>
      <Rating sop={sop} path={path} />
    </div>
  );
}
